/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import ListSubheader from '@mui/material/ListSubheader';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const ROW_H = 40;
const LIST_H = 320;

const initFind = {
  product_ids: [],
  page: 1,
  perPage: 10,
  date_start: Func.dateFirstMonth(),
  date_stop: Func.dateEndMonth(),
};

function RpProductMaterial() {
  const mod = 'rp_product_material';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [urlExcel, setUrlExcel] = useState('');
  const [urlPrint, setUrlPrint] = useState('');
  const [products, setProducts] = useState([]);
  const [codeSearch, setCodeSearch] = useState('');
  const [openProducts, setOpenProducts] = useState(false);
  const [listScroll, setListScroll] = useState(0);
  const productAnchor = useRef(null);
  const listRef = useRef(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const blank = (value) => value === null || value === undefined || value === '';

  const selectedIds = (ids) => {
    if (ids.length === 0 || (products.length > 0 && ids.length === products.length)) return '';
    return ids.join(',');
  };

  const choices = useMemo(() => {
    const query = codeSearch.trim().toLowerCase();
    if (!query) return products;
    return products.filter((item) => (item.code || '').toLowerCase().includes(query));
  }, [products, codeSearch]);

  const listStart = Math.max(0, Math.floor(listScroll / ROW_H) - 8);
  const listEnd = Math.min(choices.length, listStart + Math.ceil(LIST_H / ROW_H) + 16);

  const productLabel = () => {
    if (find.product_ids.length === 0 || find.product_ids.length === products.length) {
      return 'ทั้งหมด';
    }
    return products
      .filter((item) => find.product_ids.some((id) => String(id) === String(item.id)))
      .map((item) => item.code)
      .join(', ');
  };

  const qtyDisplay = (value) => (blank(value) ? '-' : Func.numberDisplay(value));
  const moneyCell = (value) => (blank(value) ? '-' : Func.moneyDisplay(value));

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      product_id: selectedIds(data.product_ids),
      page: data.page,
      per_page: data.perPage,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const toggleProduct = (id) => {
    const picked = find.product_ids.some((row) => String(row) === String(id));
    view({
      ...find,
      product_ids: picked
        ? find.product_ids.filter((row) => String(row) !== String(id))
        : [...find.product_ids, id],
      page: 1,
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('product_id', selectedIds(find.product_ids));
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const pdf = () => {
    const params = new URLSearchParams();
    params.append('product_id', selectedIds(find.product_ids));
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);

    const url = `${urlPrint}/download?${params.toString()}`;
    window.open(url, '_blank');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateFormat(start.toDate()),
      date_stop: Func.dateFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setProducts(result.products || []);
        view(find);
        setUrlExcel(result.excel);
        setUrlPrint(result.print);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={5} lg={4}>
            <MDBox ref={productAnchor}>
              <MDInput
                fullWidth
                value={productLabel()}
                onClick={() => setOpenProducts(true)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position='start'>{t('attr.product_code')} :</InputAdornment>
                  ),
                }}
              />
            </MDBox>
            <Menu
              anchorEl={productAnchor.current}
              open={openProducts}
              onClose={() => setOpenProducts(false)}
              disableAutoFocusItem
              PaperProps={{
                style: {
                  maxHeight: 480,
                  width: productAnchor.current ? productAnchor.current.clientWidth : undefined,
                },
              }}
            >
              <ListSubheader
                sx={{ bgcolor: 'background.paper' }}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <MDInput
                  autoFocus
                  fullWidth
                  placeholder={`${t('element.search')} ${t('attr.product_code')}`}
                  value={codeSearch}
                  onChange={(e) => {
                    setCodeSearch(e.target.value);
                    setListScroll(0);
                    if (listRef.current) listRef.current.scrollTop = 0;
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              <MenuItem
                onClick={() => {
                  const allSelected =
                    products.length > 0 && find.product_ids.length === products.length;
                  view({
                    ...find,
                    product_ids: allSelected ? [] : products.map((item) => item.id),
                    page: 1,
                  });
                }}
              >
                <Checkbox
                  checked={products.length > 0 && find.product_ids.length === products.length}
                />
                ทั้งหมด
              </MenuItem>
              <div
                ref={listRef}
                style={{
                  height: Math.min(LIST_H, Math.max(choices.length, 1) * ROW_H),
                  overflowY: 'auto',
                }}
                onScroll={(e) => setListScroll(e.target.scrollTop)}
              >
                <div style={{ height: choices.length * ROW_H, position: 'relative' }}>
                  {choices.slice(listStart, listEnd).map((item, index) => (
                    <MenuItem
                      key={item.id}
                      style={{
                        position: 'absolute',
                        top: (listStart + index) * ROW_H,
                        height: ROW_H,
                        width: '100%',
                      }}
                      onClick={() => toggleProduct(item.id)}
                    >
                      <Checkbox
                        checked={find.product_ids.some((id) => String(id) === String(item.id))}
                      />
                      {item.code}
                    </MenuItem>
                  ))}
                </div>
              </div>
            </Menu>
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg={6}>
            <MDDateRange
              dateOnly
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() =>
                view({
                  ...find,
                  date_start: Func.dateFirstMonth(),
                  date_stop: Func.dateEndMonth(),
                  page: 1,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='info' onClick={pdf}>
                <Icon>picture_as_pdf</Icon>&nbsp;PDF
              </MDButton>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table' size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' rowSpan={2}>
                      {t('attr.product_code')}
                    </TableCell>
                    <TableCell align='center' rowSpan={2}>
                      วันที่
                    </TableCell>
                    <TableCell align='center' rowSpan={2}>
                      เลขที่ใบรับ
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      รับเข้า
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      จ่าย/เบิก
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      คงเหลือ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={`${row.kind}-${row.line_id}`} hover>
                      <TableCell align='center' className='nowrap'>
                        {row.sku || '-'}
                      </TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date)}</TableCell>
                      <TableCell align='center'>{row.receive_code || '-'}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.quantity_receive)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.price_receive)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_receive)}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.quantity_issue)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.price_issue)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_issue)}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.quantity_balance)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.price_balance)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_account`), t(`menu.rp_product_material`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductMaterial;
